import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  IconButton,
  Paper,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Table,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUsers } from "@fortawesome/free-solid-svg-icons";

import { getRuta, simpleMessage } from "../../helpers/Helpers";
import { tienePermiso } from "../../helpers/permisos";
import { getToken } from "../../api/Account";
import { DataContext } from "../../context/DataContext";
import { deleteClientAsync, getClientListAsync } from "../../api/Clients";
import {
  CargandoTabla,
  ConfirmDialog,
  EstadoError,
  EstadoVacio,
  PageHeader,
  useAviso,
} from "../../components/base";
import { ERROR_OTRA_SESION, encabezadoTabla, normalizar } from "./comunes";

const FILAS_POR_PAGINA = 10;

const ClientList = () => {
  const [clientList, setClientList] = useState([]);
  const [localReload, setLocalReload] = useState(false);

  // Estado local de la lista: esqueleto mientras carga y error con "Reintentar".
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);
  const peticionActual = useRef(0);

  const { setIsLoading, setIsUnautorized, access } = useContext(DataContext);
  const aviso = useAviso();
  let ruta = getRuta();
  const token = getToken();
  let navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [pagina, setPagina] = useState(0);

  // Menú de tres puntos de una fila.
  const [menu, setMenu] = useState({ ancla: null, fila: null });

  // Confirmación de eliminar. Se conserva la fila al cerrar para que el texto no cambie
  // durante la animación de salida.
  const [confirmacion, setConfirmacion] = useState({ abierto: false, fila: null });
  const [procesando, setProcesando] = useState(false);

  const termino = normalizar(searchTerm.trim());
  const withSearch = clientList.filter((val) => {
    if (termino === "") return true;
    return (
      normalizar(val.id).includes(termino) ||
      normalizar(val.fullName).includes(termino) ||
      normalizar(val.identification).includes(termino)
    );
  });

  const cargar = async () => {
    const peticion = ++peticionActual.current;
    setCargando(true);
    setError(false);
    setIsLoading(true);
    try {
      const result = await getClientListAsync(token);
      if (peticion !== peticionActual.current) return;

      if (!result.statusResponse) {
        setIsLoading(false);
        setCargando(false);
        if (result.error === "Unauthorized") {
          setIsUnautorized(true);
          return;
        }
        setError(true);
        if (result.error === ERROR_OTRA_SESION) {
          simpleMessage(result.error, "error");
        }
        return;
      }

      setIsLoading(false);
      setClientList(result.data.result);
      setCargando(false);
    } catch (e) {
      // Sin red, `errorResponse` lanza una excepción: ni esqueleto eterno ni overlay encendido.
      if (peticion !== peticionActual.current) return;
      setIsLoading(false);
      setCargando(false);
      setError("conexion");
    }
  };

  useEffect(() => {
    cargar();
  }, [localReload]);

  // Si al buscar o eliminar la página actual queda fuera de rango, se vuelve a la primera:
  // así no aparece un "No hay datos" falso.
  useEffect(() => {
    if (pagina > 0 && pagina * FILAS_POR_PAGINA >= withSearch.length) {
      setPagina(0);
    }
  }, [pagina, withSearch.length]);

  const currentItem = withSearch.slice(
    pagina * FILAS_POR_PAGINA,
    (pagina + 1) * FILAS_POR_PAGINA
  );

  const onChangeSearch = (val) => {
    setPagina(0);
    setSearchTerm(val);
  };

  const deleteClient = async (item) => {
    setIsLoading(true);
    try {
      const result = await deleteClientAsync(token, item.id);
      if (!result.statusResponse) {
        setIsLoading(false);
        if (result.error === "Unauthorized") {
          setIsUnautorized(true);
          return;
        }
        if (result.error === ERROR_OTRA_SESION) {
          simpleMessage(result.error, "error");
          return;
        }
        aviso.error(result.error || "No se pudo eliminar el cliente");
        return;
      }

      setIsLoading(false);
      setLocalReload(!localReload);
      aviso.exito(`Cliente ${item.fullName} eliminado`);
    } catch (e) {
      setIsLoading(false);
      aviso.error("No se pudo conectar con el servidor. Inténtalo de nuevo.");
    }
  };

  const pedirEliminar = (fila) => setConfirmacion({ abierto: true, fila });
  const cerrarConfirmacion = () =>
    setConfirmacion((actual) => ({ ...actual, abierto: false }));

  // Solo aquí se llama a la función de verdad, y una sola vez por confirmación.
  const confirmar = async () => {
    setProcesando(true);
    try {
      await deleteClient(confirmacion.fila);
    } finally {
      setProcesando(false);
      cerrarConfirmacion();
    }
  };

  // Cada acción pide el permiso que exige su endpoint (ClientsController): ver el cliente es
  // GetClientById (CLIENTS VER), no UPDATE.
  const puedeAgregar = tienePermiso(access, "CLIENTS CREATE");
  const puedeVer = tienePermiso(access, "CLIENTS VER");
  const puedeEliminar = tienePermiso(access, "CLIENTS DELETE");
  const mostrarAcciones = puedeVer || puedeEliminar;

  const hayBusqueda = termino !== "";
  const filaEliminar = confirmacion.fila;

  const botonAgregar = (
    <Button
      onClick={() => {
        navigate(`${ruta}/addclient`);
      }}
      startIcon={<AddIcon />}
      variant="contained"
    >
      Agregar cliente
    </Button>
  );

  let contenido;
  if (cargando) {
    contenido = <CargandoTabla filas={8} columnas={5} etiqueta="Cargando clientes…" />;
  } else if (error) {
    contenido = (
      <EstadoError
        titulo={
          error === "conexion"
            ? "No se pudo conectar con el servidor"
            : "No se pudieron cargar los clientes"
        }
        onReintentar={cargar}
      />
    );
  } else if (withSearch.length === 0) {
    contenido = hayBusqueda ? (
      <EstadoVacio
        titulo="Sin resultados"
        descripcion={`Ningún cliente coincide con «${searchTerm.trim()}». Revisa el número, el nombre o la cédula.`}
        accion={
          <Button variant="outlined" onClick={() => onChangeSearch("")}>
            Borrar búsqueda
          </Button>
        }
      />
    ) : (
      <EstadoVacio
        titulo="Todavía no hay clientes"
        descripcion="Aquí aparecen los clientes registrados. Agrega el primero para poder darle un crédito."
        accion={puedeAgregar ? botonAgregar : undefined}
      />
    );
  } else {
    contenido = (
      <>
        <TableContainer>
          <Table aria-label="Lista de clientes">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{ ...encabezadoTabla, display: { xs: "none", sm: "table-cell" } }}
                >
                  #
                </TableCell>
                <TableCell sx={encabezadoTabla}>Cliente</TableCell>
                <TableCell
                  sx={{ ...encabezadoTabla, display: { xs: "none", md: "table-cell" } }}
                >
                  Cédula
                </TableCell>
                <TableCell
                  sx={{ ...encabezadoTabla, display: { xs: "none", md: "table-cell" } }}
                >
                  Teléfono
                </TableCell>
                <TableCell
                  sx={{
                    ...encabezadoTabla,
                    textAlign: "right",
                    display: { xs: "none", sm: "table-cell" },
                  }}
                >
                  Promedio
                </TableCell>
                {mostrarAcciones ? (
                  <TableCell sx={{ ...encabezadoTabla, textAlign: "right" }}>
                    Acciones
                  </TableCell>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {currentItem.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{
                      fontVariantNumeric: "tabular-nums",
                      display: { xs: "none", sm: "table-cell" },
                    }}
                  >
                    {row.id}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {row.fullName}
                    </Typography>
                    {/* En pantallas pequeñas la cédula y el teléfono pasan debajo del nombre. */}
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        display: { xs: "block", md: "none" },
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ display: { xs: "inline", sm: "none" } }}
                      >
                        {`#${row.id} · `}
                      </Typography>
                      {row.identification ?? "—"}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        display: { xs: "block", md: "none" },
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.phone || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      whiteSpace: "nowrap",
                      fontVariantNumeric: "tabular-nums",
                      display: { xs: "none", md: "table-cell" },
                    }}
                  >
                    {row.identification}
                  </TableCell>
                  <TableCell
                    sx={{
                      whiteSpace: "nowrap",
                      fontVariantNumeric: "tabular-nums",
                      display: { xs: "none", md: "table-cell" },
                    }}
                  >
                    {row.phone}
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                      display: { xs: "none", sm: "table-cell" },
                    }}
                  >
                    {row.promedio ?? "—"}
                  </TableCell>
                  {mostrarAcciones ? (
                    <TableCell sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      {puedeVer ? (
                        <Tooltip title="Ver cliente">
                          <IconButton
                            aria-label={`Ver al cliente ${row.fullName}`}
                            onClick={() => navigate(`${ruta}/client/${row.id}`)}
                            sx={{ color: "primary.main" }}
                          >
                            <VisibilityOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                      {puedeEliminar ? (
                        <Tooltip title="Más acciones">
                          <IconButton
                            aria-label={`Más acciones de ${row.fullName}`}
                            aria-haspopup="menu"
                            onClick={(e) => setMenu({ ancla: e.currentTarget, fila: row })}
                            sx={{ color: "text.secondary" }}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={withSearch.length}
          page={pagina}
          onPageChange={(e, nueva) => setPagina(nueva)}
          rowsPerPage={FILAS_POR_PAGINA}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          getItemAriaLabel={(tipo) =>
            ({
              first: "Primera página",
              last: "Última página",
              next: "Página siguiente",
              previous: "Página anterior",
            }[tipo])
          }
        />
      </>
    );
  }

  return (
    <Container maxWidth={false} disableGutters>
      <PageHeader
        titulo="Clientes"
        subtitulo="Busca, abre y administra los clientes"
        icono={<FontAwesomeIcon icon={faUsers} />}
        acciones={puedeAgregar ? botonAgregar : undefined}
      />

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <TextField
          size="small"
          type="search"
          onChange={(e) => onChangeSearch(e.target.value)}
          value={searchTerm}
          placeholder="Número, nombre o cédula"
          inputProps={{ "aria-label": "Buscar cliente", autoComplete: "off" }}
          sx={{ width: { xs: "100%", md: 380 }, mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon aria-hidden="true" />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Borrar búsqueda"
                  onClick={() => onChangeSearch("")}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        {contenido}
      </Paper>

      <Menu
        anchorEl={menu.ancla}
        open={Boolean(menu.ancla)}
        onClose={() => setMenu((actual) => ({ ...actual, ancla: null }))}
      >
        <MenuItem
          onClick={() => {
            const fila = menu.fila;
            setMenu((actual) => ({ ...actual, ancla: null }));
            pedirEliminar(fila);
          }}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon sx={{ color: "error.main" }}>
            <DeleteOutlineIcon />
          </ListItemIcon>
          <ListItemText>Eliminar</ListItemText>
        </MenuItem>
      </Menu>

      <ConfirmDialog
        abierto={confirmacion.abierto}
        peligro
        cargando={procesando}
        titulo="Eliminar cliente"
        mensaje={
          filaEliminar ? (
            <>
              <Typography component="p" sx={{ mb: 1 }}>
                {`Se eliminará al cliente ${filaEliminar.fullName} (cédula ${filaEliminar.identification}) con sus fotos.`}
              </Typography>
              <Typography component="p" sx={{ mb: 1 }}>
                Si el cliente tiene créditos, el sistema no lo eliminará.
              </Typography>
              <Typography component="p" sx={{ color: "text.secondary" }}>
                Esta acción no se puede deshacer.
              </Typography>
            </>
          ) : null
        }
        textoConfirmar="Eliminar cliente"
        onConfirmar={confirmar}
        onCancelar={cerrarConfirmacion}
      />
    </Container>
  );
};

export default ClientList;
